<template>
  <div class="flex w-full items-center justify-center">
    <div
      v-if="step === 'authorizing'"
      class="text-white text-xl"
    >
      Authentification e-CPS...
    </div>
    <div
      v-else-if="step === 'loading'"
      class="text-white text-xl"
    >
      Récupération des infos e-CPS...
    </div>
    <div
      v-else-if="step === 'form'"
      class="w-full"
    >
      <RegisterForm
        :ecps="ecpsInfos"
        @validate="setUserInfos($event)"
      />
    </div>
    <div
      v-else-if="step === 'map'"
      class="flex h-screen w-full"
    >
      <RegisterMapView
        v-if="postalCode"
        :postal-code="postalCode"
        :loading="loading"
        @validate="healthcenterSelected"
      />
    </div>
    <div v-else>
      <RegisterBefore />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {useRoute, useRouter} from "vue-router";
import {ref, watch} from "vue";
import {ProSanteConnectUserInfo} from "@/core/interfaces/pro-sante-connect";
import {PSC_STATES} from "@/helpers/pscHelper";
import {authenticationService, proSanteConnectApi, registrationService, storageService} from "@/container";
import electronHelper from "@/core/services/electronHelper";
import RegisterBefore from "@/register/views/RegisterBefore.vue";
import RegisterMapView from "@/register/views/RegisterMapView.vue";
import RegisterForm from "@/register/views/RegisterForm.vue";

type STEP = 'authorizing' | 'loading' | 'form' | 'map';

const PHARMACIST_CODE = '21';

const route = useRoute();
const router = useRouter();

const step = ref<STEP | undefined>(undefined);
const ecpsInfos = ref<ProSanteConnectUserInfo | null>(null);
const user = ref<any>(null);
const healthcenter = ref<any>(null);
const postalCode = ref<string | null>(null);
const loading = ref<boolean>(false);

/**
 * Handle action after pcs authentication.
 */
const handlePSCAuth = () => {
    if (route.query && route.query.code) {
        const redirect = import.meta.env.VITE_PROSANTECONNECT_CALLBACK_URL;
        step.value = 'authorizing';

        if (route.query.state === 'chu_login') {
            window.location.href = import.meta.env.VITE_CHU_PSC_CALLBACK_URL + '?code=' + route.query.code;
            return;
        } else if (route.query.state === PSC_STATES.deciphera) {
            window.location.href = import.meta.env.VITE_DECIPHERA_REGISTER_URL + '?code=' + route.query.code;
            return;
        }

        if (route.query.state === PSC_STATES.linkcps) {
            linkPSC(redirect);
        } else {
            loginRegisterPSC(redirect);
        }
    }
};

const setUserInfos = (event) => {
    user.value = event;
    postalCode.value = event.postalCode;
    step.value = 'map';
};

const healthcenterSelected = (selectedHealthcenter) => {
    healthcenter.value = selectedHealthcenter;
    const healthceterAddress = selectedHealthcenter.geo?.city?.address1 + ' ' + selectedHealthcenter.geo?.city?.postal_code + ' ' + selectedHealthcenter.geo?.city?.name;

    const registerUser = {
        access: {
            accessPointType: 2,
            active: 1,
            address: healthceterAddress,
            entityId: healthcenter.value.id,
            name: healthcenter.value.name,
            rankId: 1,
            cps: ecpsInfos.value && ecpsInfos.value.SubjectNameID
        },
        email: user.value.email,
        firstName: user.value.firstname,
        gender: user.value.gender,
        lgo: user.value.lgo,
        name: user.value.lastname,
        parrainage: user.value.referral,
        pwd: user.value.password,
        groupmentId: user.value.groupingId,
        type: 'pharmacist'
    };

    loading.value = true;
    registrationService()
        .register(registerUser)
        .then(() => {
            login();
        })
        .finally(() => {
            loading.value = false;
        });
};

const login = () => {
    if (user.value) {
        authenticationService()
            .login(user.value.email, user.value.password)
            .then(() => {
                start();
            });
    }
};

const start = () => {
    router.push({
        name: electronHelper.isElectron() ? 'app.loading' : 'onboarding'
    });

    if (window.ipcRenderer) {
        window.ipcRenderer.authenticated();
    }
};

/**
 * Function called to handle the link cps after going through the psc dedicated authentication window.
 *
 * @param redirect
 */
const linkPSC = (redirect) => {
    setTimeout(() => { // apparently PSC is not fast enough to handle code directly
        proSanteConnectApi()
            .linkCPS(route.query.code, redirect)
            .then(async (response) => {
                if (response.token) {
                    await storageService().store('token', response.token);
                    start();
                } else {
                    router.push({
                        name: 'login',
                        query: {unknownECPS: "true"}
                    });
                }
            })
            .catch(() => {
                router.push({
                    name: 'login',
                    query: {unknownECPS: "true"}
                });
            });
    }, 800);
};

/**
 * Function called to handle login and register process after going through the psc dedicated authentication window.
 *
 * @param redirect
 */
const loginRegisterPSC = (redirect) => {
    setTimeout(() => { // apparently PSC is not fast enough to handle code directly
        step.value = 'loading';
        proSanteConnectApi()
            .getToken(route.query.code, redirect)
            .then(response => {
                if (response.access_token) {
                    proSanteConnectApi()
                        .authenticate(response.access_token)
                        .then(async (response) => {
                            await storageService().store('token', response.token);
                            start();
                        })
                        .catch(() => {
                            proSanteConnectApi()
                                .getUserInfo(response.access_token)
                                .then(infos => {
                                    ecpsInfos.value = infos;
                                    if (ecpsInfos.value.SubjectRefPro.exercices[0].codeProfession !== PHARMACIST_CODE) {
                                        router.push({
                                            name: 'login',
                                            query: {
                                                noAccount: "true",
                                                noPharmacist: "true"
                                            }
                                        });
                                    }
                                })
                                .finally(() => {
                                    step.value = 'form';
                                });
                        });
                }
            })
            .catch(() => {
                backToHome();
            });
    }, 800);
};

const backToHome = () => {
    storageService().destroy('token').finally();

    router.push({name: 'login'});
};

watch(() => route.query, () => handlePSCAuth(), {immediate: true});
</script>
